// ============================================================================
// LITIGATION CASE SERVICE — Phase 9a (read-only)
// ============================================================================

import { get, patch, post } from '../client/axiosClient';
import { litigationCaseEndpoints, LITIGATION_CASE_BASE_PATH } from '../endpoints/litigationCase.endpoints';
import { buildEndpointWithQuery } from '../endpoints/base.endpoints';
import type { ApiResponse, PaginatedResponse, ID } from '../types';
import type {
  CaseParty,
  LitigationCase,
  LitigationCaseListItem,
  LitigationCaseSearchRequest,
} from '../types/litigationCase.types';

class LitigationCaseService {
  async getAll(params?: LitigationCaseSearchRequest): Promise<PaginatedResponse<LitigationCaseListItem>> {
    const endpoint = params
      ? buildEndpointWithQuery(LITIGATION_CASE_BASE_PATH, params as Record<string, any>)
      : litigationCaseEndpoints.list();

    const response = await get<any>(endpoint);
    const raw = response.data;
    const pagedResult = raw?.data ?? {};
    return {
      success: raw?.success ?? false,
      message: raw?.message,
      data: pagedResult.items ?? [],
      pagination: {
        currentPage: pagedResult.page ?? 1,
        pageSize: pagedResult.pageSize ?? 20,
        totalPages: pagedResult.totalPages ?? 0,
        totalItems: pagedResult.totalCount ?? 0,
        hasNextPage: pagedResult.hasNext ?? false,
        hasPreviousPage: pagedResult.hasPrevious ?? false,
      },
    };
  }

  async getById(id: ID): Promise<ApiResponse<LitigationCase>> {
    const response = await get<ApiResponse<LitigationCase>>(litigationCaseEndpoints.getById(id));
    return response.data;
  }

  /**
   * Claims an UNCLAIMED litigation case for the current user. Backend starts
   * the workflow instance bound to the case-type's workflow definition (from
   * mst.tbl_case_type) and stamps the claim snapshot on acct.tbl_case.
   */
  async claim(id: ID): Promise<ApiResponse<LitigationCase>> {
    const response = await post<ApiResponse<LitigationCase>>(litigationCaseEndpoints.claim(id), {});
    return response.data;
  }

  /**
   * Fetches the case's parties (PRIMARY / CO_BORROWER / GUARANTOR) for
   * pre-filling notice recipient grids. Returns an empty array if the case
   * has no account-party rows.
   */
  async getParties(id: ID): Promise<CaseParty[]> {
    const response = await get<ApiResponse<CaseParty[]>>(litigationCaseEndpoints.parties(id));
    return response.data?.data ?? [];
  }

  /** Patches the free-text Case Notes (acct.tbl_case.case_brief). */
  async updateNotes(id: ID, notes: string | null): Promise<LitigationCase> {
    const res = await patch<ApiResponse<LitigationCase>>(
      litigationCaseEndpoints.updateNotes(id),
      { notes },
    );
    return res.data.data!;
  }

  async updateMeta(id: ID, meta: {
    filingDate?: string | null;
    caseFiledAmount?: number | null;
    caseSubtype?: string | null;
    courtCaseNumber?: string | null;
  }): Promise<LitigationCase> {
    const res = await patch<ApiResponse<LitigationCase>>(
      litigationCaseEndpoints.updateMeta(id),
      meta,
    );
    return res.data.data!;
  }
}

export const litigationCaseService = new LitigationCaseService();
export default litigationCaseService;
